import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import Portal from "elements/Portal";
import { BottomSheet } from "components/bottomSheetDialog";
import { BuilderToolbarMenuPanel } from "components/builderShell/BuilderToolbarMenuPanel";
import { useAppSelector } from "hooks/redux";
import { useEditorActions, useEditorStore } from "../domain/redux/hooks";
import { selectDocument, selectSelection, selectSelectedBlockIds } from "../domain/selectors";
import { selectionHasSettingsPanelTarget } from "../domain/selection/right-panel";
import type { Selection } from "../domain/types";
import { EmailBuilderSettingsSurfaceHeader } from "../settings/EmailBuilderSettingsSurfaceHeader";
import type { SettingsBreadcrumbSegment } from "../settings/resolveSettingsBreadcrumb";
import { SettingsPanel, emailBuilderSettingsSelectionKey } from "../settings/SettingsPanel";
import { SettingsPanelHostProvider } from "../settings/settingsPanelHostContext";
import { resolveSettingsSurfaceMeta } from "../settings/settingsSurfaceMeta";
import { EMAIL_BUILDER_Z_LAYERS } from "./emailBuilderShellZLayers";
import { useEmailBuilderViewport } from "./emailBuilderViewportContext";
import { EMAIL_BUILDER_PANEL_SLIDE_MS } from "./hooks/useEmailBuilderPanelSlide";
import {
  EMAIL_BUILDER_SETTINGS_PANEL_WIDTH,
} from "./canvas/settings/emailBuilderDesktopSettingsPanel";
import {
  EMAIL_BUILDER_SETTINGS_SHEET_DEFAULT_SNAP_POINT,
  EMAIL_BUILDER_SETTINGS_SHEET_SNAP_POINTS,
} from "./canvas/settings/emailBuilderSettingsSheetSnapPoints";
import {
  EMAIL_BUILDER_SETTINGS_SHEET_OVERLAY_PORTAL_ID,
  EmailBuilderSettingsOverlayPortalProvider,
} from "./canvas/settings/emailBuilderSettingsOverlayPortal";
import "./canvas/settings/emailBuilderSettingsSidePanel.scss";
import "./canvas/settings/emailBuilderSettingsBottomSheet.scss";

function SettingsSheetExitBridge(props: { closing: boolean; onExited: () => void }): null {
  useEffect(() => {
    const closing = props.closing;
    const onExited = props.onExited;

    return () => {
      if (closing) {
        onExited();
      }
    };
  }, [props.closing, props.onExited]);

  return null;
}

function EmailBuilderClientShellSettingsSidePanel(props: {
  open: boolean;
  panelSelection: Selection;
  breadcrumbSegments: SettingsBreadcrumbSegment[] | null;
  surfaceMeta: ReturnType<typeof resolveSettingsSurfaceMeta>;
  onSelect: (selection: Selection) => void;
  onClose: () => void;
  onExitComplete?: () => void;
}) {
  const { open, panelSelection, breadcrumbSegments, surfaceMeta, onSelect, onClose, onExitComplete } = props;
  const [panelOpen, setPanelOpen] = useState(false);
  const [mounted, setMounted] = useState(open);
  const closeTimeoutRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (open) {
      const abortedExit = closeTimeoutRef.current !== null;
      if (closeTimeoutRef.current !== null) {
        window.clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      setMounted(true);
      if (abortedExit) {
        setPanelOpen(true);
        return;
      }
      const frame = window.requestAnimationFrame(() => {
        setPanelOpen(true);
      });
      return () => window.cancelAnimationFrame(frame);
    }

    setPanelOpen(false);
    closeTimeoutRef.current = window.setTimeout(() => {
      closeTimeoutRef.current = null;
      setMounted(false);
      onExitComplete?.();
    }, EMAIL_BUILDER_PANEL_SLIDE_MS);

    return () => {
      if (closeTimeoutRef.current !== null) {
        window.clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    };
  }, [onExitComplete, open]);

  const handleClose = useCallback(() => {
    const active = window.document.activeElement;
    if (active instanceof HTMLElement) {
      active.blur();
    }
    onClose();
  }, [onClose]);

  if (!mounted) {
    return null;
  }

  return (
    <Portal isMounted={true} parentId="portal-root">
      <>
        <BuilderToolbarMenuPanel
          open={panelOpen}
          panelWidth={EMAIL_BUILDER_SETTINGS_PANEL_WIDTH}
          slideTranslateExtra={10}
          className={classNames(
            "email-builder-settings-side-panel",
            "email-builder-panel",
            "email-builder-panel--fill",
            "email-builder-settings-side-panel--right",
          )}
          style={{ zIndex: EMAIL_BUILDER_Z_LAYERS.shell.underlay }}
          role="dialog"
          aria-modal={true}
          aria-label={surfaceMeta.title}
        >
          <div className="email-builder-settings-side-panel-inner">
            <EmailBuilderSettingsSurfaceHeader
              surfaceMeta={surfaceMeta}
              breadcrumbSegments={breadcrumbSegments}
              onSelect={onSelect}
              onClose={handleClose}
              className="email-builder-settings-side-panel-header"
              closeClassName="email-builder-settings-side-panel-close"
            />
            <div className="email-builder-settings-side-panel-body">
              <SettingsPanelHostProvider>
                <EmailBuilderSettingsOverlayPortalProvider
                  portalId={EMAIL_BUILDER_SETTINGS_SHEET_OVERLAY_PORTAL_ID}
                >
                  <SettingsPanel
                    key={emailBuilderSettingsSelectionKey(panelSelection)}
                    panelSelection={panelSelection}
                  />
                </EmailBuilderSettingsOverlayPortalProvider>
              </SettingsPanelHostProvider>
            </div>
          </div>
        </BuilderToolbarMenuPanel>
        <div
          id={EMAIL_BUILDER_SETTINGS_SHEET_OVERLAY_PORTAL_ID}
          className="email-builder-settings-side-panel-overlays"
        />
      </>
    </Portal>
  );
}

function EmailBuilderClientShellSettingsBottomSheet(props: {
  panelSelection: Selection;
  breadcrumbSegments: SettingsBreadcrumbSegment[] | null;
  surfaceMeta: ReturnType<typeof resolveSettingsSurfaceMeta>;
  onSelect: (selection: Selection) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { panelSelection, breadcrumbSegments, surfaceMeta, onSelect, onClose } = props;
  const sheetContentRef = useRef<HTMLDivElement | null>(null);
  const exitCompletedRef = useRef(false);
  const [open, setOpen] = useState(true);

  const blurActiveSheetField = useCallback(() => {
    const active = window.document.activeElement;
    if (active instanceof HTMLElement && sheetContentRef.current?.contains(active)) {
      active.blur();
    }
  }, []);

  const completeClose = useCallback(() => {
    if (exitCompletedRef.current) {
      return;
    }
    exitCompletedRef.current = true;
    onClose();
  }, [onClose]);

  const beginClose = useCallback(() => {
    if (!open) {
      return;
    }
    blurActiveSheetField();
    setOpen(false);
  }, [blurActiveSheetField, open]);

  useEffect(() => {
    if (open) {
      exitCompletedRef.current = false;
    }
  }, [open]);

  return (
    <BottomSheet.Root
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen) {
          beginClose();
        }
      }}
      dismissible
      modal
      repositionInputs
      snapPoints={EMAIL_BUILDER_SETTINGS_SHEET_SNAP_POINTS}
      defaultSnapPoint={EMAIL_BUILDER_SETTINGS_SHEET_DEFAULT_SNAP_POINT}
    >
      <BottomSheet.Portal>
        <BottomSheet.Backdrop />
        <BottomSheet.Popup className="email-builder-settings-bottom-sheet">
          <SettingsSheetExitBridge closing={!open} onExited={completeClose} />
          <div ref={sheetContentRef} className="email-builder-settings-bottom-sheet-content">
            <BottomSheet.Header>
              <EmailBuilderSettingsSurfaceHeader
                surfaceMeta={surfaceMeta}
                breadcrumbSegments={breadcrumbSegments}
                onSelect={onSelect}
                className="email-builder-settings-bottom-sheet-breadcrumb-header"
                showClose={false}
              />
              <BottomSheet.Close aria-label={t("Close settings")} />
            </BottomSheet.Header>
            <BottomSheet.Body className="email-builder-settings-bottom-sheet-body">
              <SettingsPanelHostProvider>
                <EmailBuilderSettingsOverlayPortalProvider
                  portalId={EMAIL_BUILDER_SETTINGS_SHEET_OVERLAY_PORTAL_ID}
                >
                  <SettingsPanel
                    key={emailBuilderSettingsSelectionKey(panelSelection)}
                    panelSelection={panelSelection}
                  />
                </EmailBuilderSettingsOverlayPortalProvider>
              </SettingsPanelHostProvider>
            </BottomSheet.Body>
          </div>
        </BottomSheet.Popup>
        <div
          id={EMAIL_BUILDER_SETTINGS_SHEET_OVERLAY_PORTAL_ID}
          className="email-builder-settings-bottom-sheet-overlays"
        />
      </BottomSheet.Portal>
    </BottomSheet.Root>
  );
}

export function EmailBuilderClientShellSettingsSurfaceHost(props: {
  mode: "design" | "preview";
}): React.ReactElement | null {
  const { mode } = props;
  const { usesBottomSheetChrome, usesDesktopRightSettingsPanel } = useEmailBuilderViewport();
  const editingMode = useAppSelector(store => store.emailBuilderStore.editingMode);
  const selection = useEditorStore(selectSelection);
  const selectedBlockIds = useEditorStore(selectSelectedBlockIds);
  const document = useEditorStore(selectDocument);
  const { setSelection } = useEditorActions();
  const { t } = useTranslation();
  const hasTarget = selectionHasSettingsPanelTarget(selection, selectedBlockIds.length);
  const [rightPanelMounted, setRightPanelMounted] = useState(false);
  const [selectionSnapshot, setSelectionSnapshot] = useState(selection);

  useEffect(() => {
    if (hasTarget) {
      setSelectionSnapshot(selection);
    }
  }, [hasTarget, selection]);

  const displaySelection = hasTarget ? selection : selectionSnapshot;

  useEffect(() => {
    if (usesDesktopRightSettingsPanel && hasTarget) {
      setRightPanelMounted(true);
    }
  }, [hasTarget, usesDesktopRightSettingsPanel]);

  const handleClose = useCallback(() => {
    setSelection({ kind: "email" });
  }, [setSelection]);

  const handleBreadcrumbSelect = useCallback(
    (nextSelection: Selection) => {
      setSelection(nextSelection);
    },
    [setSelection],
  );

  const breadcrumbSegments: SettingsBreadcrumbSegment[] | null = null;
  const surfaceMeta = useMemo(
    () => resolveSettingsSurfaceMeta(displaySelection, document, t),
    [displaySelection, document, t],
  );

  if (mode !== "design") {
    return null;
  }

  if (usesBottomSheetChrome) {
    if (editingMode !== "block-settings" || !hasTarget) {
      return null;
    }

    return (
      <EmailBuilderClientShellSettingsBottomSheet
        key={emailBuilderSettingsSelectionKey(displaySelection)}
        panelSelection={displaySelection}
        breadcrumbSegments={breadcrumbSegments}
        surfaceMeta={surfaceMeta}
        onSelect={handleBreadcrumbSelect}
        onClose={handleClose}
      />
    );
  }

  if (!usesDesktopRightSettingsPanel || !rightPanelMounted) {
    return null;
  }

  return (
    <EmailBuilderClientShellSettingsSidePanel
      open={hasTarget}
      panelSelection={displaySelection}
      breadcrumbSegments={breadcrumbSegments}
      surfaceMeta={surfaceMeta}
      onSelect={handleBreadcrumbSelect}
      onClose={handleClose}
      onExitComplete={() => setRightPanelMounted(false)}
    />
  );
}
